import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { urlFor, type SanityPost } from '@/lib/sanity';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Clock, Calendar } from 'lucide-react';

interface InsightCardProps {
  post: SanityPost;
}

export function InsightCard({ post }: InsightCardProps) {
  const readingTime = post.estimatedReadingTime || 5;
  
  return (
    <Link to={`/insights/${post.slug.current}`}>
      <Card className="group h-full overflow-hidden border-border/50 bg-card hover:border-primary/30 hover:shadow-lg transition-all duration-300">
        {/* Image */}
        {post.mainImage && (
          <div className="aspect-[16/9] overflow-hidden">
            <img
              src={urlFor(post.mainImage).width(600).height(338).url()}
              alt={post.title}
              className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          </div>
        )}
        
        <CardContent className="p-5 space-y-4">
          {/* Categories */}
          {post.categories && post.categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post.categories.slice(0, 2).map((category) => (
                <Badge 
                  key={category._id} 
                  variant="secondary"
                  className="text-xs font-medium"
                >
                  {category.title}
                </Badge>
              ))}
            </div>
          )}
          
          {/* Title */}
          <h3 className="text-lg font-semibold leading-snug text-foreground group-hover:text-primary transition-colors line-clamp-2">
            {post.title}
          </h3>
          
          {/* Excerpt */}
          {post.excerpt && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {post.excerpt}
            </p>
          )}
          
          {/* Meta */}
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            {/* Author */}
            {post.author && (
              <div className="flex items-center gap-2">
                <Avatar className="h-7 w-7">
                  {post.author.image && (
                    <AvatarImage 
                      src={urlFor(post.author.image).width(56).height(56).url()} 
                      alt={post.author.name} 
                    />
                  )}
                  <AvatarFallback className="text-xs bg-primary/10 text-primary">
                    {post.author.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-foreground">
                  {post.author.name}
                </span>
              </div>
            )}
            
            {/* Date & Reading Time */}
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {format(new Date(post.publishedAt), 'MMM d')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {readingTime} min
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
